import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms & Conditions | XtraGlass';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0b]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <motion.a
            href="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            whileHover={{ x: -4 }}
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm">Back to Home</span>
          </motion.a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Terms and Conditions
          </h1>
          <p className="text-white/50 mb-12">Last Updated: March 29, 2024</p>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="space-y-8 text-white/70">
              <div className="grid gap-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">1. Venue Rules</h3>
                  <p>Rules of the venue/organizer are applicable at all times during the event.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">2. Cover Charges</h3>
                  <p>Cover charges will be applicable as per the venue&apos;s discretion throughout the event.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">3. Alterations in Cover Charges</h3>
                  <p>The Venue holds full liberty to make alterations in cover charges throughout the event at any point in time.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">4. Last-Minute Changes</h3>
                  <p>XtraGlass does not stand accountable for any last-minute changes by the venue.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">5. Nightlife Discovery</h3>
                  <p>XtraGlass aims to help users discover nightlife based on researched data and communication with venues and their existing customers.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">6. Accuracy of Information</h3>
                  <p>XtraGlass does not stand responsible for any variation in facts or figures discovered by the user upon approaching the venue.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">7. Venue Responsibility</h3>
                  <p>Venues and organizers are solely responsible for activities at all times.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">8. Age Limit</h3>
                  <p>The required age limit to accommodate a customer can vary from venue to venue.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">9. Entry Discretion</h3>
                  <p>Entry inside is at the venue&apos;s discretion at all times.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">10. Age Verification</h3>
                  <p>Customers are required to carry valid proof of age and produce it upon venue request for verification.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">11. In-Venue Activities</h3>
                  <p>XtraGlass does not assume any accountability for the activities happening inside or outside the venue.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">12. Booking/Cancellation/Refund</h3>
                  <p>After the booking, tickets cannot be canceled, refunded or exchanged. XtraGlass reserves the right to cancel tickets in exceptional circumstances owing to any internal reason requiring such action.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">13. Availability of Services</h3>
                  <p>The service, availability of amenities mentioned and quality of events are solely the responsibility of the venues/organizers.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">14. Clauses for Entry</h3>
                  <p>Event organizers reserve the right to deny entry if a breach of policy, late entries or any restricted articles are ceased from the customer. No dangerous or potentially hazardous objects will be allowed in the venue.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">15. Security</h3>
                  <p>Security procedures, including frisking, remain the right of the management.</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-display font-bold text-white mb-2">16. Communication</h3>
                  <p>The users can reach the XtraGlass support in Help and Support section under the profile section in case of any feedback or complaints.</p>
                </div>
              </div>

              <h2 className="text-2xl font-display font-bold text-white mt-12">Contact Us</h2>
              <p>If you have any questions about these Terms and Conditions, You can contact us:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>By email: <a href="mailto:xtraglassindia@gmail.com" className="text-[#ff84e4] hover:underline">xtraglassindia@gmail.com</a></li>
                <li>Website: <a href="https://www.xtraglass.com" className="text-[#ff84e4] hover:underline" target="_blank" rel="noopener noreferrer">www.xtraglass.com</a></li>
              </ul>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-white/40 text-sm">
          © {new Date().getFullYear()} XtraGlass. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;